import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setImpersonationReturnUrl } from "shared-ui/src/utils/clientmanagement";
import { isLocalCxHost } from "../functions/urlHelper";

/**
 * Validates that a path is a safe in-app relative path:
 * - must start with exactly one "/"
 * - must not start with "//" (protocol-relative URL)
 * - must not contain ":" (e.g. "http:")
 */
function isSafeRedirectPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.includes(":");
}

/**
 * Validates that a return URL is a well-formed absolute http(s) URL before
 * it is persisted. The origin itself is re-validated later, when the saved
 * value is actually used to redirect (see handleImpersonateAccount.ts).
 */
function isSafeReturnUrl(url: string): boolean {
  try {
    return ["http:", "https:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

/**
 * Local-development-only bridge: reads impersonation params placed in the URL
 * by the Customer Success frontend and writes them into this origin's localStorage
 * before redirecting within CX.
 *
 * Supports two impersonation modes:
 *   - Account: autotaskAccountId (sets impersonateAccount)
 *   - User:    userId or email (sets impersonateUser)
 *
 * An optional `redirectPath` param controls where to land after the bridge
 * runs. Defaults to "/".
 *
 * An optional `returnUrl` param (the Customer Success URL to restore on
 * return to Customer Success) is persisted into this origin's sessionStorage
 * via setImpersonationReturnUrl, which always uses sessionStorage (never
 * localStorage) so concurrent impersonation tabs don't overwrite each
 * other's return URL. This is unlike impersonateUser/impersonateAccount/
 * demoData below, which use shared localStorage directly in production.
 *
 * In production both frontends share a domain so localStorage is directly
 * accessible and this route is never invoked.
 */
export function ImpersonationBridge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Only allow this bridge on local/dev hosts; in production the two
    // frontends share a domain so localStorage is directly accessible.
    if (!isLocalCxHost()) {
      navigate("/", { replace: true });
      return;
    }

    localStorage.removeItem("impersonateUser");
    localStorage.removeItem("impersonateAccount");
    localStorage.removeItem("demoData");

    const autotaskAccountId = searchParams.get("autotaskAccountId");
    const userId = searchParams.get("userId");
    const email = searchParams.get("email");
    const demoData = searchParams.get("demoData");
    const returnUrl = searchParams.get("returnUrl");
    const rawRedirectPath = searchParams.get("redirectPath") ?? "/";
    const redirectPath = isSafeRedirectPath(rawRedirectPath) ? rawRedirectPath : "/";

    if (autotaskAccountId) {
      localStorage.setItem("impersonateAccount", autotaskAccountId);
    } else if (userId || email) {
      localStorage.setItem("impersonateUser", JSON.stringify({ userId, email }));
    }

    if (demoData === "true") {
      localStorage.setItem("demoData", "true");
    }

    if (returnUrl && isSafeReturnUrl(returnUrl)) {
      setImpersonationReturnUrl(returnUrl);
    }

    navigate(redirectPath, { replace: true });
  }, []);

  return null;
}
